import { FC, useEffect, useState } from "react";
import {
  Control,
  FieldValues,
  UseFormGetValues,
  UseFormHandleSubmit,
  UseFormRegister,
  UseFormReset,
  UseFormSetValue,
} from "react-hook-form";
import { MenuItem, Select, TextField } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useMutation } from "react-query";
import { fetchGetTaskCounterCompanies, fetchGetTaskCounterOrganizations, fetchGetTaskCounterOrganizationsByCompany } from "../miscellaneous/fetchGetOrganizations";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { SearchButton } from "../../common/components/SearchButton";
import { TaskCounterTemplateCriteria } from "../miscellaneous/taskCounterTemplateSearchAtom";

type SearchTaskCounterProps = {
  control: Control<FieldValues, any, FieldValues>;
  register: UseFormRegister<FieldValues>;
  handleSubmit: UseFormHandleSubmit<FieldValues, FieldValues>;
  getValues: UseFormGetValues<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  reset: UseFormReset<FieldValues>;
  setSearchParams: any;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  defaultValues?: TaskCounterTemplateCriteria;
  onAtomReset?: () => void;
};

export const SearchTaskCounter: FC<SearchTaskCounterProps> = ({
  control,
  register,
  handleSubmit,
  getValues,
  setValue,
  reset,
  setSearchParams,
  setPage,
  defaultValues,
  onAtomReset,
}) => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(defaultValues?.companyId ?? "");
  const [selectedOrgId, setSelectedOrgId] = useState(defaultValues?.organizationId ?? "");
  const [jobTitleSearch, setJobTitleSearch] = useState(defaultValues?.jobTitle ?? "");

  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];
  const isSuperAdmin = userRole[0]?.Name.toLowerCase() === "super admin";
  const isRoleAdmin = userRole[0]?.Name.toLowerCase() === "admin";
  const callerParentCompanyId = localStorage.getItem("parentCompanyId") ?? "";


  const mutationGetCompanies = useMutation(fetchGetTaskCounterCompanies, {
    onSuccess: (res) => {
      if (res.status === 200) {
        setCompanies(res.data ?? []);
      }
    },
    onError: () => {},
  });

  const mutationGetOrgsByCompany = useMutation(fetchGetTaskCounterOrganizationsByCompany, {
    onSuccess: (res) => {
      if (res.status === 200) {
        setOrganizations(res.data ?? []);
      }
    },
    onError: () => {},
  });

  const mutationGetAllOrgs = useMutation(fetchGetTaskCounterOrganizations, {
    onSuccess: (res) => {
      if (res.status === 200) {
        setOrganizations(res.data ?? []);
      }
    },
    onError: () => {},
  });

  useEffect(() => {
    if (isSuperAdmin) {
      mutationGetCompanies.mutate();
    } else if (isRoleAdmin) {
      mutationGetCompanies.mutate();
      setSelectedCompanyId(defaultValues?.companyId || callerParentCompanyId);
    } else {
      mutationGetCompanies.mutate(undefined);
    }
    if (defaultValues?.companyId) {
      mutationGetOrgsByCompany.mutate(defaultValues.companyId);
    } else {
      mutationGetAllOrgs.mutate();
    }
    // eslint-disable-next-line
  }, []);

  const handleCompanyChange = (companyId: string) => {
    setSelectedCompanyId(companyId);
    setSelectedOrgId("");
    if (companyId) {
      mutationGetOrgsByCompany.mutate(companyId);
    } else {
      mutationGetAllOrgs.mutate();
    }
  };

  const handleReset = () => {
    reset({});
    setPage(0);
    setSelectedCompanyId("");
    setSelectedOrgId("");
    setJobTitleSearch("");
    mutationGetAllOrgs.mutate();
    setSearchParams({});
    onAtomReset?.();
  };

  const onSearchSubmit = () => {
    setPage(0);
    setSearchParams({
      companyId: selectedCompanyId,
      organizationId: selectedOrgId,
      jobTitle: jobTitleSearch,
    });
  };

  return (
    <div className="mt-9 border-2 pt-7 pb-7">
      <form onSubmit={handleSubmit(onSearchSubmit)}>
        <div className="flex flex-row justify-center items-center gap-8 flex-wrap">
          <div className="flex flex-row justify-center items-center">
            <label className="font-bold mr-3">Company</label>
            <Select
              id="company-filter"
              value={selectedCompanyId}
              onChange={(e) => handleCompanyChange(e.target.value)}
              style={{ height: "38px", width: "220px" }}
              displayEmpty
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {companies.map((item: any) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.companyName}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className="flex flex-row justify-center items-center">
            <label className="font-bold mr-3">Organization</label>
            <Select
              id="organization-filter"
              value={selectedOrgId}
              onChange={(e) => setSelectedOrgId(e.target.value)}
              style={{ height: "38px", width: "220px" }}
              displayEmpty
            >
              <MenuItem value="">
                <em>All</em>
              </MenuItem>
              {organizations.map((item: any) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.companyName}
                </MenuItem>
              ))}
            </Select>
          </div>

          <div className="flex flex-row justify-center items-center">
            <label className="font-bold mr-3">Job Title</label>
            <TextField
              value={jobTitleSearch}
              onChange={(e) => setJobTitleSearch(e.target.value)}
              placeholder="Search job title..."
              sx={{
                width: "220px",
                "& .MuiInputBase-input": {
                  height: "5px",
                },
              }}
            />
          </div>
        </div>

        <div className="flex flex-center justify-center mt-10 gap-5">
          <ButtonCancelA
            name="Reset"
            onHandleClick={handleReset}
            icon={<ClearIcon />}
          />
          <SearchButton />
        </div>
      </form>
    </div>
  );
};
